// src/utility/context/ability.js
import { AbilityBuilder, createMongoAbility } from '@casl/ability'

export const SUBJECTS = {
  DASHBOARD: 'Dashboard',
  TRANSACTION: 'Transaction',
  RULE: 'Rule',
  RULE_BUILDER: 'RuleBuilder',
  RULE_SANDBOX: 'RuleSandbox',

  ALERT: 'Alert',
  MERCHANT: 'Merchant',
  AUDIT_LOG: 'AuditLog',
  ORCHESTRATION: 'Orchestration',
  REPORT: 'Report',
  USER: 'User',
  USER_MGMT: 'UserManagement',
  ROLE_MGMT: 'RoleManagement',
  SETTINGS: 'Settings',
  ENTITY_LIST: 'EntityList',
  ALL: 'all',
}

export const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  EXECUTE: 'execute',
}

export function buildAbility(role) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility)

  switch (role) {

    case 'SUPER_ADMIN':
      can('manage', 'all')   // full access
      break

    case 'RISK_ADMIN':
      // Full read everywhere
      can(ACTIONS.READ, 'all')
      // Rules: full CRUD + sandbox
      can(ACTIONS.CREATE, SUBJECTS.RULE)
      can(ACTIONS.UPDATE, SUBJECTS.RULE)
      can(ACTIONS.DELETE, SUBJECTS.RULE)
      can(ACTIONS.UPDATE, SUBJECTS.RULE_BUILDER)

      can(ACTIONS.EXECUTE, SUBJECTS.RULE_SANDBOX)
      // Alerts: full action
      can(ACTIONS.UPDATE, SUBJECTS.ALERT)
      // Merchants: update + whitelist
      can(ACTIONS.UPDATE, SUBJECTS.MERCHANT)
      // Reports: export
      can(ACTIONS.EXPORT, SUBJECTS.REPORT)
      // Users: read only
      can(ACTIONS.READ, SUBJECTS.USER)
      break

    case 'RISK_ANALYST':
      // Read everywhere
      can(ACTIONS.READ, 'all')

      // Alerts: can take actions (update)
      can(ACTIONS.UPDATE, SUBJECTS.ALERT)
      // Sandbox: read + execute
      can(ACTIONS.READ, SUBJECTS.RULE_BUILDER)

      can(ACTIONS.EXECUTE, SUBJECTS.RULE_SANDBOX)
      // Cannot create/edit rules
      cannot(ACTIONS.CREATE, SUBJECTS.RULE)
      cannot(ACTIONS.UPDATE, SUBJECTS.RULE)
      cannot(ACTIONS.DELETE, SUBJECTS.RULE)
      cannot(ACTIONS.READ, SUBJECTS.USER_MGMT)
      cannot(ACTIONS.READ, SUBJECTS.ROLE_MGMT)
      cannot(ACTIONS.READ, SUBJECTS.SETTINGS)
      cannot(ACTIONS.READ, SUBJECTS.AUDIT_LOG)
      cannot(ACTIONS.READ, SUBJECTS.ENTITY_LIST)
      break

    case 'OPERATIONS':
      // Read everywhere
      can(ACTIONS.READ, 'all')

      // Rule Builder: read only
      cannot(ACTIONS.READ, SUBJECTS.RULE_BUILDER)

      // Explicitly deny Rule Sandbox
      cannot(ACTIONS.READ, SUBJECTS.RULE_SANDBOX)
      cannot(ACTIONS.EXECUTE, SUBJECTS.RULE_SANDBOX)

      // Merchants
      can(ACTIONS.UPDATE, SUBJECTS.MERCHANT)

      // Reports
      can(ACTIONS.EXPORT, SUBJECTS.REPORT)

      // Rule Management: no modifications
      cannot(ACTIONS.CREATE, SUBJECTS.RULE)
      cannot(ACTIONS.UPDATE, SUBJECTS.RULE)
      cannot(ACTIONS.DELETE, SUBJECTS.RULE)

      // Rule Builder: no modifications
      cannot(ACTIONS.UPDATE, SUBJECTS.RULE_BUILDER)

      // Alerts: cannot take actions
      cannot(ACTIONS.UPDATE, SUBJECTS.ALERT)
      cannot(ACTIONS.READ, SUBJECTS.USER_MGMT)
      cannot(ACTIONS.READ, SUBJECTS.ROLE_MGMT)
      cannot(ACTIONS.READ, SUBJECTS.SETTINGS)
      cannot(ACTIONS.READ, SUBJECTS.AUDIT_LOG)
      cannot(ACTIONS.READ, SUBJECTS.ENTITY_LIST)

      break

    case 'VIEWER':
    default:
      // READ ONLY on all pages — no writes anywhere
      can(ACTIONS.READ, SUBJECTS.DASHBOARD)
      can(ACTIONS.READ, SUBJECTS.TRANSACTION)
      can(ACTIONS.READ, SUBJECTS.RULE)
      can(ACTIONS.READ, SUBJECTS.ALERT)
      can(ACTIONS.READ, SUBJECTS.MERCHANT)
      // can(ACTIONS.READ, SUBJECTS.AUDIT_LOG)
      can(ACTIONS.READ, SUBJECTS.ORCHESTRATION)
      can(ACTIONS.READ, SUBJECTS.REPORT)
      // can(ACTIONS.READ, SUBJECTS.SANDBOX)
      // Explicitly block all writes
      cannot(ACTIONS.CREATE, 'all')
      cannot(ACTIONS.UPDATE, 'all')
      cannot(ACTIONS.DELETE, 'all')
      cannot(ACTIONS.EXPORT, 'all')
      cannot(ACTIONS.EXECUTE, 'all')
      break
  }

  return build()
}

export const ROUTE_ABILITY_MAP = {
  '/dashboard': { action: ACTIONS.READ, subject: SUBJECTS.DASHBOARD },
  '/rule-management': {
    action: ACTIONS.READ,
    subject: SUBJECTS.RULE,
  },

  '/rule-builder': {
    action: ACTIONS.READ,
    subject: SUBJECTS.RULE_BUILDER,
  },

  '/rule-sandbox': {
    action: ACTIONS.READ,
    subject: SUBJECTS.RULE_SANDBOX,
  },
  '/entity-lists': {
    action: ACTIONS.READ,
    subject: SUBJECTS.ENTITY_LIST,
  },
  '/orchestration': { action: ACTIONS.READ, subject: SUBJECTS.ORCHESTRATION },
  '/transactions': { action: ACTIONS.READ, subject: SUBJECTS.TRANSACTION },
  '/alerts': { action: ACTIONS.READ, subject: SUBJECTS.ALERT },
  '/reports': { action: ACTIONS.READ, subject: SUBJECTS.REPORT },
  '/merchants': { action: ACTIONS.READ, subject: SUBJECTS.MERCHANT },
  '/settings': { action: ACTIONS.READ, subject: SUBJECTS.SETTINGS },
  '/audit-log': { action: ACTIONS.READ, subject: SUBJECTS.AUDIT_LOG },
  '/user-management': { action: ACTIONS.READ, subject: SUBJECTS.USER_MGMT },
  '/role-management': { action: ACTIONS.READ, subject: SUBJECTS.ROLE_MGMT },
}